import { createApi } from '@reduxjs/toolkit/query/react'
import {
  AddAppraisalEventRequest,
  AddAppraisalFeedbackRequest,
  AddAppraisalGoalRequest,
  AppraisalEvent,
  AppraisalGoal,
  AppraisalResponseCompleteInfo,
  AppraisalResponseDetails,
  ConfirmReplyRequest,
  ConfirmResponseRequest,
  DownloadAppraisalResponsesImportTemplateRequest,
  EditAppraisalEventRequest,
  EditAppraisalGoalRequest,
  GetAppraisalEventCandidatesRequest,
  ImportAppraisalResponsesRequest,
  ImportHistoricalAppraisalRequest,
  InviteEmployeeToAppraisalEventRequest,
  ListDescendantAppraisalResponseDetailsRequest,
  RemoveAppraisalEventsRequest,
  RemoveAppraisalGoalsRequest,
  RemoveEmployeeAppraisalResponsesRequest,
  ReplyAppraisalEventRequest,
  ReplyApprovalRequest,
  SubmitPerformanceRatingRequest,
  UpdateApproversRequest,
  UpdateReviewerRequest,
} from '@/features/appraisal/appraisal.def.ts'
import { hcPortalBaseQuery } from '@/features/baseQuery.ts'
import { EmployeeEligibleOption } from '@/features/employee-data/employee-data.def.ts'
import { HcError } from '@/common/types/api.def.ts'
import { downloadFile } from '@/utils/misc.ts'

export const appraisalApi = createApi({
  reducerPath: 'appraisal',
  baseQuery: hcPortalBaseQuery(),
  tagTypes: ['AppraisalEvent', 'AppraisalResponseDetails', 'AppraisalGoal'],
  endpoints: (builder) => ({
    getAppraisalEvents: builder.query<AppraisalEvent[], void>({
      query: () => ({
        url: 'Appraisal/GetAppraisalEvents',
      }),
      providesTags: () => [{ type: 'AppraisalEvent', id: 'LIST' }],
    }),
    getAppraisalEventById: builder.query<AppraisalEvent, number>({
      query: (appraisalEventId: number) => ({
        url: 'Appraisal/GetAppraisalEventById',
        params: {
          appraisalEventId,
        },
      }),
      providesTags: (result) => [{ type: 'AppraisalEvent', id: result?.id }],
    }),
    downloadAppraisalResponses: builder.mutation<void, number>({
      query: (appraisalEventId: number) => ({
        url: 'Appraisal/DownloadAppraisalResponses',
        params: {
          appraisalEventId,
        },
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadFile(`appraisal_event_${appraisalEventId}.xlsx`, blob)
        },
      }),
    }),
    downloadAppraisalResponsesImportTemplate: builder.mutation<void, DownloadAppraisalResponsesImportTemplateRequest>({
      query: (request) => ({
        url: 'Appraisal/DownloadAppraisalResponsesImportTemplate',
        method: 'POST',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadFile(`appraisal_event_${request.appraisalEventId}_template.xlsx`, blob)
        },
      }),
    }),
    importAppraisalResponses: builder.mutation<void, ImportAppraisalResponsesRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('appraisalEventId', request.appraisalEventId.toString())
        formData.append('excelFile', request.excelFile)

        return {
          url: '/Appraisal/ImportAppraisalResponses',
          method: 'POST',
          cache: 'no-cache',
          body: formData,
        }
      },
      invalidatesTags: (_result, _error, request) => {
        return [{
          type: 'AppraisalResponseDetails',
          id: `${request.appraisalEventId}_LIST`,
        }]
      },
    }),
    addAppraisalEvent: builder.mutation<boolean, AddAppraisalEventRequest>({
      query: (request) => ({
        url: '/Appraisal/AddAppraisalEvent',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: () => [{ type: 'AppraisalEvent', id: 'LIST' }],
    }),
    editAppraisalEvent: builder.mutation<boolean, EditAppraisalEventRequest>({
      query: (request) => ({
        url: '/Appraisal/EditAppraisalEvent',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [{ type: 'AppraisalEvent', id: request.id }],
    }),
    removeAppraisalEvents: builder.mutation<boolean, RemoveAppraisalEventsRequest>({
      query: (request) => ({
        url: '/Appraisal/RemoveAppraisalEvents',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [{
        type: 'AppraisalEvent',
        id: 'LIST',
      }, ...(request.appraisalEventIds.map(id => {
        return {
          type: 'AppraisalEvent' as const,
          id: `${id}`,
        }
      }))],
    }),
    listAppraisalResponseDetails: builder.query<AppraisalResponseDetails[], number>({
      query: (appraisalEventId: number) => ({
        url: 'Appraisal/ListAppraisalResponseDetails',
        params: {
          appraisalEventId,
        },
      }),
      providesTags: (_result, _error, appraisalEventId) => [{
        type: 'AppraisalResponseDetails',
        id: `${appraisalEventId}_LIST`,
      }],
    }),
    getAppraisalResponseDetailsById: builder.query<AppraisalResponseCompleteInfo, number>({
      query: (responseId: number) => ({
        url: 'Appraisal/GetAppraisalResponseDetailsById',
        params: {
          responseId,
        },
      }),
      providesTags: (_result, _error, responseId) => [{
        type: 'AppraisalResponseDetails',
        id: `${responseId}`,
      }],
    }),
    inviteEmployeeToAppraisalEvent: builder.mutation<boolean, InviteEmployeeToAppraisalEventRequest>({
      query: (request) => ({
        url: '/Appraisal/InviteEmployeeToAppraisalEvent',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        {
          type: 'AppraisalResponseDetails',
          id: `${request.appraisalEventId}_LIST`,
        },
      ],
    }),
    removeEmployeeAppraisalResponses: builder.mutation<boolean, RemoveEmployeeAppraisalResponsesRequest>({
      query: (request) => ({
        url: '/Appraisal/RemoveEmployeeAppraisalResponses',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        {
          type: 'AppraisalResponseDetails',
          id: `${request.appraisalEventId}_LIST`,
        },
        ...(request.responseIds.map(id => {
          return {
            type: 'AppraisalResponseDetails' as const,
            id: `${id}`,
          }
        })),
      ],
    }),
    getAppraisalGoals: builder.query<AppraisalGoal[], void>({
      query: () => ({
        url: 'Appraisal/GetAppraisalGoals',
      }),
      providesTags: () => [{ type: 'AppraisalGoal', id: 'LIST' }],
    }),
    getAppraisalGoalById: builder.query<AppraisalGoal, number>({
      query: (goalId: number) => ({
        url: 'Appraisal/GetAppraisalGoalById',
        params: {
          goalId,
        },
      }),
      providesTags: (result) => [{ type: 'AppraisalGoal', id: result?.id }],
    }),
    addAppraisalGoal: builder.mutation<boolean, AddAppraisalGoalRequest>({
      query: (request) => ({
        url: '/Appraisal/AddAppraisalGoal',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: () => [{ type: 'AppraisalGoal', id: 'LIST' }],
    }),
    editAppraisalGoal: builder.mutation<boolean, EditAppraisalGoalRequest>({
      query: (request) => ({
        url: '/Appraisal/EditAppraisalGoal',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [{ type: 'AppraisalGoal', id: request.id }],
    }),
    removeAppraisalGoals: builder.mutation<boolean, RemoveAppraisalGoalsRequest>({
      query: (request) => ({
        url: '/Appraisal/RemoveAppraisalGoals',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [{
        type: 'AppraisalGoal',
        id: 'LIST',
      }, ...(request.goalIds.map(id => {
        return {
          type: 'AppraisalGoal' as const,
          id: `${id}`,
        }
      }))],
    }),
    getAppraisalEventCandidates: builder.query<EmployeeEligibleOption[], GetAppraisalEventCandidatesRequest>({
      query: (request) => ({
        url: '/Appraisal/GetAppraisalEventCandidates',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
    }),
    addAppraisalFeedback: builder.mutation<boolean, AddAppraisalFeedbackRequest>({
      query: (request) => ({
        url: '/Appraisal/AddAppraisalFeedback',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'AppraisalResponseDetails', id: request.responseId }]
      },
    }),
    importHistoricalAppraisal: builder.mutation<void, ImportHistoricalAppraisalRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('excelFile', request.excelFile)

        return {
          url: '/Appraisal/ImportHistoricalAppraisal',
          method: 'POST',
          cache: 'no-cache',
          body: formData,
        }
      },
      invalidatesTags: () => {
        return [{ type: 'AppraisalGoal', id: 'LIST' }]
      },
    }),
    listOwnAppraisalResponseDetails: builder.query<AppraisalResponseDetails[], void>({
      query: () => ({
        url: `/Appraisal/ListOwnAppraisalResponseDetails`,
        method: 'GET',
        cache: 'no-cache',
      }),
      providesTags: () => {
        return [{ type: 'AppraisalResponseDetails', id: 'LIST' }]
      },
    }),
    listDescendantAppraisalResponseDetails: builder.query<AppraisalResponseDetails[], ListDescendantAppraisalResponseDetailsRequest>(
      {
        query: (request) => ({
          url: `/Appraisal/ListDescendantAppraisalResponseDetails`,
          method: 'GET',
          cache: 'no-cache',
          params: request,
        }),
        providesTags: () => {
          return [{ type: 'AppraisalResponseDetails', id: 'DESCENDANT_LIST' }]
        },
      }),
    listDescendantActionItems: builder.query<AppraisalResponseDetails[], ListDescendantAppraisalResponseDetailsRequest>(
      {
        query: (request) => ({
          url: `/Appraisal/ListDescendantActionItems`,
          method: 'GET',
          cache: 'no-cache',
          params: request,
        }),
        providesTags: () => {
          return [{ type: 'AppraisalResponseDetails', id: 'DESCENDANT_ACTION_REQUIRED_LIST' }]
        },
      }),
    replyAppraisalEvent: builder.mutation<boolean, ReplyAppraisalEventRequest>({
      query: (request) => ({
        url: '/Appraisal/ReplyAppraisalEvent',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'AppraisalResponseDetails', id: request.responseId }]
      },
    }),
    updateReviewer: builder.mutation<boolean, UpdateReviewerRequest>({
      query: (request) => ({
        url: '/Appraisal/UpdateReviewer',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'AppraisalResponseDetails', id: request.responseId }]
      },
    }),
    updateApprovers: builder.mutation<boolean, UpdateApproversRequest>({
      query: (request) => ({
        url: '/Appraisal/UpdateApprovers',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'AppraisalResponseDetails', id: request.responseId }]
      },
    }),
    replyApproval: builder.mutation<boolean, ReplyApprovalRequest>({
      query: (request) => ({
        url: '/Appraisal/ReplyApproval',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
    }),
    confirmReply: builder.mutation<boolean, ConfirmReplyRequest>({
      query: (request) => ({
        url: '/Appraisal/ConfirmReply',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'AppraisalResponseDetails', id: request.responseId }]
      },
    }),
    confirmReplyAsEmployee: builder.mutation<boolean, ConfirmReplyRequest>({
      query: (request) => ({
        url: '/Appraisal/ConfirmReplyAsEmployee',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'AppraisalResponseDetails', id: request.responseId }]
      },
    }),
    acknowledgeReplyAsEmployee: builder.mutation<boolean, ConfirmReplyRequest>({
      query: (request) => ({
        url: '/Appraisal/AcknowledgeReplyAsEmployee',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'AppraisalResponseDetails', id: request.responseId }]
      },
    }),
    submitPerformanceRating: builder.mutation<boolean, SubmitPerformanceRatingRequest>({
      query: (request) => ({
        url: '/Appraisal/SubmitPerformanceRating',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'AppraisalResponseDetails', id: request.responseId }]
      },
    }),
    confirmResponse: builder.mutation<boolean, ConfirmResponseRequest>({
      query: (request) => ({
        url: '/Appraisal/ConfirmResponse',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => {
        return [{ type: 'AppraisalResponseDetails', id: request.responseId }]
      },
    }),
  }),
})

export const {
  useGetAppraisalEventsQuery,
  useGetAppraisalEventByIdQuery,
  useDownloadAppraisalResponsesMutation,
  useDownloadAppraisalResponsesImportTemplateMutation,
  useListAppraisalResponseDetailsQuery,
  useImportAppraisalResponsesMutation,
  useAddAppraisalEventMutation,
  useEditAppraisalEventMutation,
  useRemoveAppraisalEventsMutation,
  useGetAppraisalResponseDetailsByIdQuery,
  // useAddAppraisalEventResponseMutation,
  useListDescendantAppraisalResponseDetailsQuery,
  useListDescendantActionItemsQuery,
  useInviteEmployeeToAppraisalEventMutation,
  useRemoveEmployeeAppraisalResponsesMutation,
  useGetAppraisalGoalsQuery,
  useGetAppraisalGoalByIdQuery,
  useAddAppraisalGoalMutation,
  useEditAppraisalGoalMutation,
  useRemoveAppraisalGoalsMutation,
  useGetAppraisalEventCandidatesQuery,
  useAddAppraisalFeedbackMutation,
  useImportHistoricalAppraisalMutation,
  useListOwnAppraisalResponseDetailsQuery,
  useReplyAppraisalEventMutation,
  useUpdateReviewerMutation,
  useUpdateApproversMutation,
  useReplyApprovalMutation,
  useConfirmReplyMutation,
  useConfirmReplyAsEmployeeMutation,
  useAcknowledgeReplyAsEmployeeMutation,
  useSubmitPerformanceRatingMutation,
  useConfirmResponseMutation,
} = appraisalApi
